import { Component, Input, OnInit } from '@angular/core';
import { BlockUI, NgBlockUI }       from 'ng-block-ui';
import { NgForm }                   from '@angular/forms';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { CodeTypeModel }                           from '../core/models/code-type';
import { CommunicationCenterContactModel }         from './typings/communication-center.contact';
import { CommunicationCenterFiltersModel }         from './typings/communication-center.filters';
import { CommunicationCenterMessagesModel }        from './typings/communication-center.messages';
import { CommunicationCenterSelectedContactModel } from './typings/communication-center.selected-contact';
import { CommunicationCenterService }              from './communication-center.service';
import { CustomerSourceModel }                     from '../company-lists/typings/customer-source';
import { MessagesService }                         from '../messages/messages.service';
import { RecpatchaService }                        from '../recaptcha/recaptcha.service';
import { SessionService }                          from '../sessions/session.service';
import { Table }                                   from '../table/types/table';
import { TableQueryParameters }                    from '../table/types/table-query-parameters';
import { TableService }                            from '../table/table.service';
import { TemplatedWordingService }                 from '../templates/templated-wording.service';
import { TemplateModel }                           from '../templates/typings/template';

@Component({
  selector   : 'communication-center',
  template: require('./communication-center.component.html')
})
export class CommunicationCenterComponent implements OnInit {
  @BlockUI('contactsBlock') block : NgBlockUI;

  @Input() communicationTypes: Array<CodeTypeModel>;
  @Input() contactTypes      : Array<CodeTypeModel>;
  @Input() customerSources   : Array<CustomerSourceModel>;

  messages : CommunicationCenterMessagesModel = {
    document_message: '',
    email_message   : '',
    email_subject   : '',
    sms_message     : ''
  };

  filters = {
    communicationType: 'customer',
    contactType      : '*'
  } as CommunicationCenterFiltersModel;

  regoLabel : string = this.sessionService.countrySettings().rego_label;

  contacts        : Array<CommunicationCenterContactModel>         = [];
  selectedContacts: Array<CommunicationCenterSelectedContactModel> = [];

  tableSettings : Table = {
    cache: {
      enabled: true,
      key    : this.communicationCenterService.tableCacheKey
    },
    columns: [],
    empty  : {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_CONTACTS')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.communicationCenterService.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 25, 50 ],
      queryCount: () => this.communicationCenterService.getContactsCount(this.filters)
    },
    rows: {
      highlight: {
        enabled: true
      },
      select: {
        enabled: true,
        multi  : true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.communicationCenterService.tableCacheKey, {
      direction: 'asc',
      field    : 'display_name'
    })
  };

  constructor (
    private communicationCenterService: CommunicationCenterService,
    private messagesService           : MessagesService,
    private recpatchaService          : RecpatchaService,
    private router                    : UIRouter,
    private sessionService            : SessionService,
    private tableService              : TableService,
    private templatedWordingService   : TemplatedWordingService,
    private translateService          : TranslateService
  ) {}

  addTemplates ( type : string ) : void {
    this.templatedWordingService.addTemplate(type)
    .then(( templates : Array<TemplateModel> ) => {
      templates.forEach(template => {
        const wording = String(template.wording).replace(/<[^>]+>/gm, '');

        switch ( type ) {
          case 'sms':
            this.messages.sms_message = this.messages.sms_message
              ? `${ this.messages.sms_message }\n\n${ wording }`
              : wording;
            break;

          case 'email':
            this.messages.email_message = this.messages.email_message
              ? `${ this.messages.email_message }\n\n${ wording }`
              : wording;
            break;

          case 'document':
            this.messages.document_message = this.messages.document_message
              ? `${ this.messages.document_message }\n\n${ wording }`
              : wording;
            break;
        }
      });
    });
  }

  buildColumns () : void {
    const baseColumns = [
      {
        field : 'display_name',
        header: {
          text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER')
        }
      },
      {
        field : 'email',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.EMAIL')
        }
      },
      {
        field : 'mobile',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.MOBILE')
        }
      },
      {
        field : 'contact_type',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.SEND_TYPE')
        }
      }
    ];

    const vehicleColumns = [
      {
        field : 'plate_number',
        header: {
          text: this.regoLabel
        }
      },
      {
        field : 'make',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MAKE')
        }
      },
      {
        field : 'model',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MODEL')
        }
      },
      {
        field : 'build_date',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.YEAR')
        }
      }
    ];

    this.tableSettings.columns.splice(0);

    this.tableSettings.columns.push(...baseColumns);

    if ( this.isVehicle() ) {
      this.tableSettings.columns.push(...vehicleColumns);
    }
  }

  getContacts ( params : TableQueryParameters ) : void {
    this.communicationCenterService.getContacts(this.filters, params)
    .then(( contacts : Array<CommunicationCenterContactModel> ) => {
      this.contacts = contacts;

      this.updateSelection();
    })
    .catch(error => this.messagesService.error(error));
  }

  isCommunicationType ( type : string ) : boolean {
    return this.filters.communicationType === type;
  }

  isContactType ( type : string ) : boolean {
    return this.filters.contactType === type;
  }

  isVehicle () : boolean {
    return this.filters.communicationType === 'vehicle'
      || this.filters.communicationType === 'last_in';
  }

  ngOnInit () : void {
    const company = this.sessionService.company();

    if ( company.company_variable_fields.plate_number_name ) {
      this.regoLabel = company.company_variable_fields.plate_number_name;
    }

    this.buildColumns();
  }

  onRowClick ( contact : CommunicationCenterContactModel ) : void {
    const selectedContact : CommunicationCenterSelectedContactModel = {
      customer_id             : contact.customer_id,
      preferred_contact_method: contact.contact_type,
      ...(this.isVehicle()
        ? {
            vehicle_id: contact.vehicle_id
          }
        : {})
    };

    if ( contact.selected ) {
      const exists = this.selectedContacts.some(c => this.isVehicle()
        ? c.vehicle_id === contact.vehicle_id
        : c.customer_id === contact.customer_id
      );

      if ( !exists ) {
        this.selectedContacts.push(selectedContact);
      }
    }
    else {
      this.selectedContacts = this.selectedContacts.filter(c => this.isVehicle()
        ? c.vehicle_id !== contact.vehicle_id
        : c.customer_id !== contact.customer_id
      );
    }
  }

  search () : void {
    this.contacts = [];

    this.buildColumns();
    this.selectNone();

    this.getContacts({
      direction: this.tableService.getCachedSorting(this.communicationCenterService.tableCacheKey).direction,
      field    : this.tableService.getCachedSorting(this.communicationCenterService.tableCacheKey).field,
      pageSize : this.tableService.getCachedPaging(this.communicationCenterService.tableCacheKey).pageSize
    });
  }

  selectAll () : void {
    this.block.start();

    this.communicationCenterService.getContactsCount(this.filters)
    .then(( count : number ) => this.communicationCenterService.getContacts(this.filters, {
      pageSize: count
    }))
    .then(( contacts : Array<CommunicationCenterContactModel> ) => {
      this.selectedContacts = [];

      return contacts.forEach(contact => {
        contact.selected = true;

        this.onRowClick(contact);
      });
    })
    .then(() => this.updateSelection())
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  selectNone () : void {
    this.selectedContacts = [];

    this.updateSelection();
  }

  send ( form : NgForm ) : void {
    this.recpatchaService.verifyUser()
    .then(() => this.communicationCenterService.send(this.messages, this.selectedContacts, this.filters))
    .then(( data : any ) => {
      if ( this.filters.contactType === 'print' || ( this.filters.contactType === '*' && data.document ) ) {
        window.open(this.communicationCenterService.getDocumentURL());
      }

      if ( data.warning ) {
        this.messagesService.show(data.message, 'warning');
      }
      else {
        this.messagesService.show(data.message, 'success');
      }

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  updateSelection () : void {
    this.contacts.forEach(contact => {
      contact.selected = this.selectedContacts.some(c => this.isVehicle()
        ? c.vehicle_id === contact.vehicle_id
        : c.customer_id === contact.customer_id
      );
    });
  }
}
